import { Badge } from "@package/subframe/components/Badge";
import { Button } from "@package/subframe/components/Button";
import { IconButton } from "@package/subframe/components/IconButton";
import { Loader } from "@package/subframe/components/Loader";
import {
  FeatherArrowLeft,
  FeatherCheckCircle,
  FeatherDownload,
  FeatherListTodo,
  FeatherSnowflake,
} from "@subframe/core";
import {
  createFileRoute,
  useNavigate,
  useParams,
} from "@tanstack/react-router";
import { format } from "date-fns";
import { useState } from "react";
import { toast } from "sonner";

import { IncidentStatusBadge } from "@/components/incidents/IncidentStatusBadge";
import { IncidentTimeline } from "@/components/incidents/IncidentTimeline";
import { LinkedFollowUpActions } from "@/components/incidents/LinkedFollowUpActions";
import { SignOffIncidentDialog } from "@/components/incidents/SignOffIncidentDialog";
import {
  getIncidentPdfQueryControllerGet,
  useGetIncidentQueryControllerGet,
} from "@/generated/out-of-range-incidents/out-of-range-incidents";
import { downloadUrl } from "@/lib/download";

export const Route = createFileRoute("/_authenticated/incidents/$incidentId")({
  component: IncidentDetailPage,
});

function IncidentDetailPage() {
  const { incidentId } = useParams({
    from: "/_authenticated/incidents/$incidentId",
  });
  const navigate = useNavigate();
  const [signOffOpen, setSignOffOpen] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  const { data, isLoading, error } =
    useGetIncidentQueryControllerGet(incidentId);
  const incident = data?.data;

  const handleDownloadPdf = async () => {
    setDownloadingPdf(true);
    try {
      const res = await getIncidentPdfQueryControllerGet(incidentId);
      const url = (res?.data as { url?: string } | undefined)?.url;
      if (!url) throw new Error("No PDF URL returned");

      downloadUrl(url);
      // Browsers don't fire a JS event when the download itself starts/finishes
      // for cross-origin signed URLs, so the click() returning is the closest
      // proxy to "the download was initiated". A success toast here gives the
      // user feedback that the action did something — without it, on slow
      // networks the page sits silent for a moment after the spinner clears.
      toast.success("PDF ready — check your downloads");
    } catch {
      toast.error("Failed to generate PDF — please try again");
    } finally {
      setDownloadingPdf(false);
    }
  };

  const handleBack = () => {
    if (window.history.length > 1) {
      window.history.back();
    } else {
      navigate({ to: "/dashboard" });
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader size="large" />
      </div>
    );
  }

  if (error || !incident) {
    return (
      <div className="flex w-full flex-col items-start gap-4">
        <IconButton icon={<FeatherArrowLeft />} onClick={handleBack} />
        <div className="flex w-full flex-col items-start gap-2 rounded-md border border-neutral-200 bg-white p-6">
          <span className="text-body font-body text-default-font">
            Incident not found or you don&apos;t have access.
          </span>
        </div>
      </div>
    );
  }

  const isOpen = incident.resolvedAt === null;
  // Sign-off is locked until the latest reading is back in range.
  // `!== false` keeps the button usable if the field is ever absent (older
  // API) — the server 409 is the backstop.
  const canSignOff = incident.canSignOff !== false;

  const goToCreateAction = () => {
    navigate({
      to: "/follow-up-actions/create",
      search: {
        outOfRangeIncidentId: incident.id,
        nurseryId: incident.nurseryId,
        coldStorageName: incident.coldStorageName,
        scheduledQuestionnaireId: "",
        answerId: "",
        questionText: "",
        checklistName: "",
      },
    });
  };

  return (
    <div className="flex w-full flex-col items-start bg-white animate-fade-in">
      {/* Header */}
      <div className="flex w-full flex-col items-start gap-1 pb-6">
        <div className="flex w-full flex-wrap items-center gap-4">
          <div className="flex grow shrink-0 basis-0 flex-col items-start gap-1">
            <div className="flex w-full items-center gap-2">
              <IconButton icon={<FeatherArrowLeft />} onClick={handleBack} />
              <FeatherSnowflake className="h-5 w-5 text-warning-800" />
              <span className="grow shrink-0 basis-0 whitespace-nowrap text-metrics font-metrics text-primary-navy-800">
                {incident.coldStorageName}
              </span>
              <IncidentStatusBadge resolvedAt={incident.resolvedAt} />
            </div>
            <span className="text-body font-body text-subtext-color">
              Opened {format(new Date(incident.openedAt), "PPp")}
              {incident.resolvedAt && (
                <>
                  {" · resolved "}
                  {format(new Date(incident.resolvedAt), "PPp")}
                </>
              )}
            </span>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="flex w-full max-w-3xl mx-auto flex-col items-start gap-6 pb-8">
        {/* Action bar — PDF download is available in both states; sign-off
            and follow-up only while open. All three buttons use size="medium"
            + explicit h-10 so the variants (outlined vs filled) don't render
            at different heights. */}
        <div className="flex w-full flex-wrap items-center justify-end gap-2">
          <Button
            className="h-10"
            variant="neutral-secondary"
            size="medium"
            icon={<FeatherDownload />}
            onClick={handleDownloadPdf}
            disabled={downloadingPdf}
          >
            {downloadingPdf ? "Generating PDF…" : "Download PDF"}
          </Button>
          {isOpen && (
            <>
              <Button
                className="h-10"
                variant="neutral-secondary"
                size="medium"
                icon={<FeatherListTodo />}
                onClick={goToCreateAction}
              >
                Create follow-up task
              </Button>
              <Button
                className="h-10"
                variant="brand-primary"
                size="medium"
                icon={<FeatherCheckCircle />}
                onClick={() => setSignOffOpen(true)}
                disabled={!canSignOff}
              >
                Sign off incident
              </Button>
            </>
          )}
          {isOpen && !canSignOff && (
            <span className="w-full text-right text-caption font-caption text-subtext-color">
              Record an in-range reading for this fridge before signing off.
            </span>
          )}
        </div>

        {/* Triggering reading callout */}
        <section className="flex w-full flex-col items-start gap-3 rounded-md border border-warning-200 bg-warning-50 p-4">
          <span className="text-heading-3 font-heading-3 text-default-font">
            Triggering reading
          </span>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-body-bold font-body-bold text-default-font">
              {incident.firstReading.temperature}°C
            </span>
            <Badge variant="warning">Out of range</Badge>
            {incident.firstReading.thresholdMinAtLog !== null &&
              incident.firstReading.thresholdMaxAtLog !== null && (
                <span className="text-caption font-caption text-subtext-color">
                  target {incident.firstReading.thresholdMinAtLog}°C to{" "}
                  {incident.firstReading.thresholdMaxAtLog}°C
                </span>
              )}
          </div>
          {incident.firstReading.notes && (
            <span className="text-caption font-caption text-default-font">
              “{incident.firstReading.notes}”
            </span>
          )}
          <span className="text-caption font-caption text-subtext-color">
            Recorded {format(new Date(incident.firstReading.recordedAt), "PPp")}
          </span>
        </section>

        {/* Resolution panel (only when resolved) */}
        {!isOpen && (
          <section className="flex w-full flex-col items-start gap-3 rounded-md border border-success-200 bg-success-50 p-4">
            <span className="text-heading-3 font-heading-3 text-default-font">
              Resolution
            </span>
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant="success">Signed off</Badge>
              <span className="text-caption font-caption text-subtext-color">
                {incident.resolvedAt &&
                  format(new Date(incident.resolvedAt), "PPp")}
              </span>
            </div>
            {incident.resolutionNotes ? (
              <p className="text-body font-body text-default-font whitespace-pre-wrap">
                {incident.resolutionNotes}
              </p>
            ) : (
              <span className="text-caption font-caption text-subtext-color">
                No remedial-action notes recorded.
              </span>
            )}
          </section>
        )}

        {/* Linked follow-up tasks (renders only when there are any) */}
        <LinkedFollowUpActions
          nurseryId={incident.nurseryId}
          incidentId={incident.id}
        />

        {/* Incident timeline — readings interleaved with the full lifecycle
            audit trail (alert, follow-up tasks, sign-off) so a manager has the
            complete picture without opening the PDF export (NAU-219). */}
        <section className="flex w-full flex-col items-start gap-3">
          <span className="text-heading-3 font-heading-3 text-default-font">
            Incident timeline
          </span>
          <IncidentTimeline incident={incident} />
        </section>
      </div>

      <SignOffIncidentDialog
        open={signOffOpen}
        onOpenChange={setSignOffOpen}
        incidentId={incident.id}
        coldStorageName={incident.coldStorageName}
        onSignedOff={handleBack}
      />
    </div>
  );
}
